import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { customCategoriesAPI, subscriptionAPI, enumsAPI } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Pencil, Trash2, Tags, Crown, Loader2, Check, X,
} from "lucide-react";

export default function CustomCategories() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [defaultCategories, setDefaultCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPro, setIsPro] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [catRes, subRes, defaultRes] = await Promise.all([
        customCategoriesAPI.list(),
        subscriptionAPI.status(),
        enumsAPI.modCategoriesPublic(),
      ]);
      setCategories(catRes.data.categories || []);
      setIsPro(subRes.data.is_pro);
      setDefaultCategories(defaultRes.data || []);
    } catch {
      toast.error("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      await customCategoriesAPI.create({ name: trimmed, type: "mod" });
      toast.success(`"${trimmed}" added`);
      setNewName("");
      setAddOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add category");
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async (id) => {
    const trimmed = editName.trim();
    if (!trimmed) return;
    try {
      await customCategoriesAPI.rename(id, { name: trimmed, type: "mod" });
      toast.success("Category renamed");
      setEditingId(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to rename");
    }
  };

  const handleDelete = async (id) => {
    try {
      await customCategoriesAPI.delete(id);
      toast.success("Category deleted");
      setDeleteConfirmId(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isPro) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4">
        <Crown className="w-12 h-12 text-amber-500" />
        <h2 className="text-xl font-bold">Pro Feature</h2>
        <p className="text-muted-foreground text-center">Custom categories are available with a Pro subscription.</p>
        <Button onClick={() => navigate("/subscription")}>Upgrade to Pro</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                <Tags className="w-5 h-5 text-primary" />
                Custom Categories
              </h1>
              <p className="text-xs text-muted-foreground">{categories.length} custom categories</p>
            </div>
          </div>
          <Button onClick={() => setAddOpen(true)} data-testid="add-category-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Category
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Custom Categories */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Crown className="w-4 h-4 text-amber-500" />
              Your Custom Categories
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {categories.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No custom categories yet. Click "Add Category" to create one.
              </p>
            ) : (
              categories.map((cat) => (
                <div
                  key={cat.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-border"
                  data-testid={`category-item-${cat.id}`}
                >
                  {editingId === cat.id ? (
                    <div className="flex items-center gap-2 flex-1">
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="h-8"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleRename(cat.id);
                          if (e.key === "Escape") setEditingId(null);
                        }}
                        data-testid="rename-input"
                      />
                      <Button size="sm" variant="ghost" onClick={() => handleRename(cat.id)} data-testid="rename-confirm">
                        <Check className="w-4 h-4 text-green-500" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <span className="font-medium text-sm">{cat.name}</span>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { setEditingId(cat.id); setEditName(cat.name); }}
                          data-testid={`rename-btn-${cat.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        {deleteConfirmId === cat.id ? (
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="destructive" onClick={() => handleDelete(cat.id)} data-testid="delete-confirm">
                              Delete
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setDeleteConfirmId(null)}>
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDeleteConfirmId(cat.id)}
                            data-testid={`delete-btn-${cat.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 text-red-400" />
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Default Categories (read-only) */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Default Categories</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {defaultCategories.map((cat) => (
                <Badge key={cat} variant="secondary" className="text-xs">
                  {cat}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Add Category Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Custom Category</DialogTitle>
            <DialogDescription>Create a new mod category for your build sheets.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="new-cat">Category Name</Label>
            <Input
              id="new-cat"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g., Communications, Recovery Gear"
              data-testid="new-category-input"
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAdd(); } }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={saving || !newName.trim()} data-testid="save-category-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Add Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
